import { useEffect, useRef } from 'react';
import type { Locale, ThemePreference } from '@bulk/types';
import { api } from './api';
import { useAuth } from './auth';
import { useI18n } from './i18n';
import { useTheme } from './theme';

function stored(key: string): boolean {
  try {
    return localStorage.getItem(key) !== null;
  } catch {
    return false;
  }
}

/**
 * Theme and language belong to the person, not the browser. On sign-in the account's choice wins — unless
 * the account still holds the default and this browser holds an explicit choice made before the account
 * could remember one; then the browser's choice is saved to the account. After that, a change made
 * anywhere (the settings page, a language switch, the account menu) is saved back.
 */
export function PreferencesSync() {
  const { user, refresh } = useAuth();
  const { locale, setLocale } = useI18n();
  const { preference, setPreference } = useTheme();
  const adopted = useRef<string | null>(null);
  const saving = useRef(false);

  useEffect(() => {
    if (!user) {
      adopted.current = null;
      return;
    }
    const patch: { locale?: Locale; theme?: ThemePreference } = {};
    if (adopted.current !== user.id) {
      adopted.current = user.id;
      if (user.locale !== locale) {
        if (user.locale === 'en' && stored('bulk.locale')) patch.locale = locale;
        else setLocale(user.locale);
      }
      if (user.theme !== preference) {
        if (user.theme === 'system' && stored('bulk.theme')) patch.theme = preference;
        else setPreference(user.theme);
      }
    } else {
      if (user.locale !== locale) patch.locale = locale;
      if (user.theme !== preference) patch.theme = preference;
    }
    if (Object.keys(patch).length === 0 || saving.current) return;
    saving.current = true;
    void api('/api/v1/auth/me', { method: 'PATCH', body: patch })
      .then(() => refresh())
      .catch(() => undefined)
      .finally(() => {
        saving.current = false;
      });
  }, [user, locale, preference, setLocale, setPreference, refresh]);

  return null;
}
